import React from 'react';
import { Paper } from './Paper';
import { resolveSx } from './style-utils.js';
import './paper.less';

export interface CardProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  component?: React.ElementType;
  elevation?: number;
  sx?: React.CSSProperties | Record<string, unknown>;
}

export const Card = React.forwardRef<HTMLElement, CardProps>(({ className, elevation = 1, ...props }, ref) => (
  <Paper
    ref={ref}
    elevation={elevation}
    className={['ls-card', 'MuiCard-root', className || ''].filter(Boolean).join(' ')}
    {...props}
  />
));
Card.displayName = 'Card';

export interface CardSlotProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  component?: React.ElementType;
  sx?: React.CSSProperties | Record<string, unknown>;
}

export const CardContent = React.forwardRef<HTMLElement, CardSlotProps>(({
  as,
  component,
  sx,
  style,
  className,
  children,
  ...props
}, ref) => {
  const Element: React.ElementType = component || as || 'div';
  const resolved = resolveSx(sx);
  return (
    <Element
      ref={ref}
      className={['ls-card-content', 'MuiCardContent-root', resolved.className || '', className || ''].filter(Boolean).join(' ')}
      style={{ ...resolved.style, ...style }}
      {...props}
    >
      {children}
    </Element>
  );
});
CardContent.displayName = 'CardContent';

export interface CardMediaProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  component?: React.ElementType;
  image?: string;
  src?: string;
  height?: number | string;
  sx?: React.CSSProperties | Record<string, unknown>;
}

export const CardMedia = React.forwardRef<HTMLElement, CardMediaProps>(({
  as,
  component,
  image,
  src,
  height,
  sx,
  style,
  className,
  children,
  ...props
}, ref) => {
  const Element: React.ElementType = component || as || 'div';
  const isMediaElement = typeof Element === 'string' && ['img', 'video', 'audio', 'picture', 'iframe'].includes(Element);
  const resolved = resolveSx(sx);
  const resolvedStyle = {
    ...(height != null ? { height: typeof height === 'number' ? `${height}px` : height } : null),
    ...(!isMediaElement && (image || src) ? { backgroundImage: `url("${image || src}")` } : null),
    ...resolved.style,
    ...style,
  };

  return (
    <Element
      ref={ref}
      className={['ls-card-media', resolved.className || '', className || ''].filter(Boolean).join(' ')}
      style={resolvedStyle}
      {...(isMediaElement ? { src: image || src } : null)}
      {...props}
    >
      {children}
    </Element>
  );
});
CardMedia.displayName = 'CardMedia';

export const CardActions = React.forwardRef<HTMLElement, CardSlotProps>(({
  as,
  component,
  sx,
  style,
  className,
  children,
  ...props
}, ref) => {
  const Element: React.ElementType = component || as || 'div';
  const resolved = resolveSx(sx);
  return (
    <Element
      ref={ref}
      className={['ls-card-actions', resolved.className || '', className || ''].filter(Boolean).join(' ')}
      style={{ ...resolved.style, ...style }}
      {...props}
    >
      {children}
    </Element>
  );
});
CardActions.displayName = 'CardActions';

export default Card;
